import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { MessageSquare, LayoutGrid, Users, CheckSquare, MessageCircle, Activity, Settings, LogOut } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { useSidebar } from "./ui/sidebar";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarSeparator,
} from "./ui/sidebar"; 
import React from "react";

const mainMenuItems = [
  { title: "Dashboard", icon: LayoutGrid, url: "/app/dashboard" },
  { title: "Meetings", icon: Users, url: "/app/meetings" },
  { title: "Action Items", icon: CheckSquare, url: "/app/action-items" },
  { title: "Thoughts", icon: MessageCircle, url: "/app/thoughts" },
  { title: "Status", icon: Activity, url: "/app/status" },
];

const initialTags = [
  { id: 1, name: "SFMC", color: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300" },
  { id: 2, name: "Stand-up", color: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300" },
  { id: 3, name: "Project", color: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" },
  { id: 4, name: "Important", color: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300" },
  { id: 5, name: "Badge", color: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300" },
];

export function AppSidebar() {
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [tags] = useState(initialTags);
  const { state } = useSidebar();

  return (
    <Sidebar>
      <SidebarContent className="flex flex-col h-full bg-slate-100 dark:bg-slate-900 border-r">
        <div className="p-4">
          <Link to="/" className="flex items-center gap-2 mb-6">
            <MessageSquare className="h-6 w-6" />
            <span className="font-semibold text-lg">MeetMemoize</span>
          </Link>

          <SidebarMenu>
            {mainMenuItems.map((item) => {
              const isActive = location.pathname === item.url;
              return (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <Link
                      to={item.url}
                      className={`flex items-center gap-3 px-2 py-2 rounded-full transition-colors hover:bg-accent hover:text-accent-foreground ${
                        isActive ? "bg-accent text-accent-foreground font-medium" : "text-muted-foreground"
                      }`}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </div>

        <SidebarSeparator />

        <div className="mt-auto border-t">
          <div className="p-4">
            <Link
              to="/app/settings/profile"
              className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.user_metadata?.avatar_url} />
                <AvatarFallback>
                  {user?.user_metadata?.first_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  {`${user?.user_metadata?.first_name || ''} ${user?.user_metadata?.last_name || ''}`}
                </span>
                <span className="text-xs text-muted-foreground">View profile</span>
              </div>
            </Link>

            <div className="mt-2 space-y-1">
              <Link 
                to="/app/settings"
                className="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-muted-foreground rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <Settings className="h-4 w-4" />
                Settings
              </Link>
              <button
                onClick={signOut}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-destructive rounded-md hover:bg-destructive/10 cursor-pointer transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      </SidebarContent>
    </Sidebar>
  );
}